'use strict';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import {getCurrentChatModalId} from '../../comments/reducers/state';
import {actions as chatChannelModalActions} from '../../comments/redux/chatChannelModal';
import {videoSessionsActions} from '../../comments/redux/videoSessionRequests';
import {commentsStates} from '../../comments/router';
import {APPLICATION_SERVER_PUBLIC_KEY} from '../../config/constants';
import {getRouter, getStateName} from '../../config/reducers/state';
import {getId} from '../../core-utilities/domain/ReduxUtils';
import {truncateName} from '../../core-utilities/upload-service/FileUtilities';
import {logger} from './logger';


const registerServiceWorker = require('serviceworker-loader!../../../service-worker/sw.js');

MobilePushService.$inject = [
  '$window',
  'EventBus',
  '$state',
  '$settings',
  '$ngRedux',
  '$toast',
  'DeviceServices',
  'UserDomain',
  'fetchUtils',
  'authCacheService',
];

export function MobilePushService(
  $window,
  EventBus,
  $state,
  $settings,
  $ngRedux,
  $toast,
  DeviceServices,
  UserDomain,
  fetchUtils,
  authCacheService,
) {
  this.init = init;
  this.enablePush = enablePush;
  this.disablePush = disablePush;

  function init() {
    // Check for updates
    getServiceWorkerRegistration();
    EventBus.on('event:UserAvailable', _withUser);
  }

  function _withUser(event, user) {
    if ($settings.enablePushNotifications)
      enablePush(getId(user));
  }

  function enablePush(userId) {
    if (DeviceServices.isWebView()) {
      initPush(userId);
      return Promise.resolve(null); // Ionic.Push does not return a promise
    } else {
      return initChromePush(userId);
    }
  }

  function disablePush(userId) {
    return authCacheService.getItem('device_push_token')
      .catch(err => null)
      .then(existingToken => {
        if (DeviceServices.isWebView() && existingToken && (existingToken.token || existingToken._token)) {
          return UserDomain.removePushToken(userId, existingToken).then(onDisablePushSuccess, onDisabledPushFail);
        } else if (!DeviceServices.isWebView() && existingToken) {
          return UserDomain.removeWebPushToken(userId, existingToken).then(onDisablePushSuccess, onDisabledPushFail);
        } else {
          return null;
        }
      })
  }

  function onEnabledPushFail(msg) {
    return err => {
      logger.error({err}, `Failed to register for push notifications at ${msg}. error=${err && err.message}`);
      $settings.enablePushNotifications = false;
    };
  }

  function logWriteFailure(msg) {
    return err => {
      logger.error({err}, `Failed to save push notification subscription at ${msg}. error=${err && err.message}`);
    };
  }

  function onDisablePushSuccess() {
    // overwrite the existing token
    authCacheService.removeItem('device_push_token')
      .catch(err => {
        logger.warn(`Failed to remove key="device_push_token" from authCacheService with error="${err && err.message}"`);
      });
  }

  function onDisabledPushFail(err) {
    logger.error({err}, `Failed to unregister for push notifications. error=${err && err.message}`);
  }

  function getServiceWorkerRegistration() {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      return registerServiceWorker({scope: '/'}).then(reg => {
        reg.update();

        return reg;
      });
    } else {
      return Promise.resolve(null);
    }
  }

  function initChromePush(userId) {
    return getServiceWorkerRegistration()
      .then(reg => {
        if (!reg)
          return null;

        logger.debug('Registered for push notifications');

        return reg.pushManager.getSubscription()
          .then(subscription => isValidSubscription(subscription))
          .then(isValid => {
            if (isValid) {
              logger.debug('User is already subscribed.');
            } else {
              return subscribeDesktopUser(reg, userId);
            }
          });
      })
      .catch(onEnabledPushFail('initChromePush'));
  }

  /**
   * Check if the provided subscription is valid.
   * @param subscription
   * @return {Promise<Boolean>}
   */
  function isValidSubscription(subscription) {
    if (subscription !== null) {
      const endpoint = subscription.endpoint;
      return authCacheService.getItem('device_push_token')
        .catch(err => null)
        .then(existingToken => endpoint === existingToken)
    } else {
      return Promise.resolve(false);
    }
  }

  function subscribeDesktopUser(reg, userId) {
    return reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(APPLICATION_SERVER_PUBLIC_KEY),
    }).then((subscription) => {
      const endpoint = subscription.endpoint;
      return authCacheService.getItem('device_push_token')
        .catch(err => null)
        .then(existingToken => {
          if (endpoint !== existingToken) {
            if (existingToken) {
              UserDomain.removeWebPushToken(userId, existingToken);
            }

            return UserDomain.addWebPushToken(userId, JSON.stringify(subscription))
              .then(() => {
                // overwrite the existing token
                authCacheService.setItem('device_push_token', endpoint)
                  .catch(logWriteFailure('subscribeDesktopUser'));
              }, onEnabledPushFail('subscribeDesktopUser'));
          }
        });
    });
  }

  function urlB64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  function initPush(userId) {
    const push = new Ionic.Push({
      debug: false,
      canShowAlert: false, //Can pushes show an alert on your screen?
      canSetBadge: true, //Can pushes update app icon badges?
      canPlaySound: true, //Can notifications play a sound?
      canRunActionsOnWake: true, //Can run actions outside the app,
      onNotification: function onNotification(data) {
        logger.debug({data}, 'received notification');
        const additionalData = get(data, '_raw.additionalData');

        try {
          if (isObject(additionalData)) {
            logger.debug({payload: additionalData}, 'notification payload');
            const foreground = !data.app.wasAsleep && !data.app.wasClosed;
            switch (additionalData.type) {
              case 'chat':
                onNewChat(data.title, data.text, additionalData, foreground);
                break;

              case 'video-chat-invite':
                onVideoChat(data.title, data.text, additionalData, foreground);
                break;
            }
          }
        } catch (err) {
          logger.error({err}, `Ionic.Push.onNotification failed. error=${err && err.message}`);
        }
      },
      onRegister: function onRegister(pushToken) {
        logger.debug('Registered token:', pushToken);
        authCacheService.getItem('device_push_token')
          .catch(err => null)
          .then(existingToken => {
            if ((pushToken && pushToken.token && pushToken.token) !== (existingToken && (existingToken.token || existingToken._token))) {
              if (existingToken) {
                logger.debug('Device push token changed');
                UserDomain.removePushToken(userId, existingToken);
              }
              UserDomain.addPushToken(userId, pushToken)
                .then(() => {
                  // overwrite the existing token
                  authCacheService.setItem('device_push_token', pushToken)
                    .catch(logWriteFailure('Ionic.Push.onRegister'));
                }, onEnabledPushFail('Ionic.Push.onRegister'));
            }
          });

      },
      onError: function (err) {
        try {
          // this is a curried function so we have to call it twice
          fetchUtils.logFallbackUsage({msg: 'push.onError', isWebView: DeviceServices.isWebView(), userId, err})(null);
        } catch (err) {
          logger.error({err}, `Ionic.Push.onError failed. error=${err && err.message}`);
        }
      },
      pluginConfig: {
        ios: {
          badge: true,
          sound: true,
          categories: {
            videoCall: {
              yes: {
                callback: 'acceptCall',
                title: 'Accept',
                foreground: true,
                destructive: false,
              },
              no: {
                callback: 'rejectCall',
                title: 'Reject',
                foreground: false,
                destructive: true,
              },
            },
          },
        },
        android: {
          iconColor: '#343434'
        }
      }
    });

    push.register();

    if (push._plugin) {
      push._plugin.on('acceptCall', function (data) {
        console.log('accepted call with data:', data);
        const additionalData = get(data, '_raw.additionalData');
        openVideoChat(additionalData);

        push.finish(
          function () {
            console.log('processing of push data is finished');
          },
          function () {
            console.log(
              'something went wrong with push.finish for ID = ' +
              data.additionalData.notId || 1
            );
          },
          data.additionalData.notId || 1
        );
      });

      push._plugin.on('rejectCall', function (data) {
        // TODO: respond to server that user rejected chat invite
        push.finish(
          function () {
            console.log('processing of push data is finished');
          },
          function () {
            console.log(
              'something went wrong with push.finish for ID = ' +
              data.additionalData.notId || 1
            );
          },
          data.additionalData.notId || 1
        );
      });
    }
  }

  function onNewChat(title, message, payload, foreground) {
    if (isString(payload.targetId)) {
      if (getCurrentChatModalId($ngRedux.getState()) === payload.targetId) {
        return;
      } else if (foreground) {
        // TODO: add message to the in-app notification panel
        $toast.showMessage(truncateName(message ? message : "You've received a new message", 40), title ? title : null, $toast.TOAST_TYPES.info, {
          toastOptions: {
            autoClose: 12500,
          },
          action: {
            className: 'button button-small button-balanced',
            label: 'Show',
            handler: showChannel.bind(null, payload.targetId),
          },
        });
      } else {
        showChannel(payload.targetId)
      }
    }
  }

  function showChannel(channelId) {
    $ngRedux.dispatch(chatChannelModalActions.showModal({
      parentId: channelId,
      allowNav: true,
    }))
  }

  function isValidVideoChat(payload) {
    return isObject(payload)
      && isString(payload.channelId)
      && isString(payload.sessionId);
  }

  function onVideoChat(title, message, payload, foreground) {
    if (isValidVideoChat(payload)) {
      const rootState = $ngRedux.getState();
      const routerState = getRouter(rootState);
      if (getStateName(rootState) === commentsStates.videoChat && get(routerState, 'currentParams.sessionId') === payload.sessionId) {
        return;
      } else if (foreground) {
        // TODO: add message to the in-app notification panel
        $toast.showMessage(truncateName(message ? message : "You've been invited to a video chat", 40), title ? title : null, $toast.TOAST_TYPES.info, {
          toastOptions: {
            autoClose: 12500,
          },
          action: {
            className: 'button button-small button-balanced',
            label: 'Answer',
            handler: openVideoChat.bind(null, payload),
          },
        });
      } else {
        // Delay nav while app is restarting to lower chance of failure
        setTimeout(() => openVideoChat(payload), 30 * 1000);
      }
    }
  }

  function openVideoChat(payload) {
    if (isValidVideoChat(payload)) {
      $ngRedux.dispatch(videoSessionsActions.navToVideo({
        channelId: payload.channelId,
        sessionId: payload.sessionId,
      }));
    }
  }

  function onState(payload) {
    //if(payload["$state"]) {
    //  //prompt the user to switch
    //  navigator.notification.confirm("You have a new chat - go to it?", function(btn) {
    //    if(btn === 1) {
    //      $state.go(payload["$state"]);
    //    }
    //  },"New Chat!")
    //}
  }
}



// WEBPACK FOOTER //
// ./src/js/core-utilities-implementation/push-notifications/MobilePushService.js